import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/lib/api';
import { CircleNotch, ClockCounterClockwise, CaretLeft, CaretRight, Funnel } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const actionLabels = {
  de: {
    favorite_add: 'Favorit hinzugefügt', favorite_remove: 'Favorit entfernt', tags_update: 'Tags aktualisiert',
    template_create: 'Vorlage erstellt', csv_export: 'CSV exportiert', bulk_import: 'Bulk Import',
    device_connect: 'Gerät verbunden', script_execute: 'Skript ausgeführt', device_update: 'Gerät aktualisiert',
    device_delete: 'Gerät gelöscht', user_create: 'Benutzer erstellt', user_delete: 'Benutzer gelöscht',
    tenant_create: 'Mandant erstellt', tenant_delete: 'Mandant gelöscht',
  },
  en: {
    favorite_add: 'Favorite added', favorite_remove: 'Favorite removed', tags_update: 'Tags updated',
    template_create: 'Template created', csv_export: 'CSV exported', bulk_import: 'Bulk import',
    device_connect: 'Device connected', script_execute: 'Script executed', device_update: 'Device updated',
    device_delete: 'Device deleted', user_create: 'User created', user_delete: 'User deleted',
    tenant_create: 'Tenant created', tenant_delete: 'Tenant deleted',
  },
};

const actionColors = {
  favorite_add: 'text-yellow-400 bg-yellow-500/10', favorite_remove: 'text-yellow-400 bg-yellow-500/10',
  tags_update: 'text-blue-400 bg-blue-500/10', template_create: 'text-purple-400 bg-purple-500/10',
  csv_export: 'text-green-400 bg-green-500/10', bulk_import: 'text-cyan-400 bg-cyan-500/10',
  device_connect: 'text-emerald-400 bg-emerald-500/10', script_execute: 'text-orange-400 bg-orange-500/10',
  device_update: 'text-blue-400 bg-blue-500/10', device_delete: 'text-red-400 bg-red-500/10',
};

export default function AuditLogPage() {
  const { language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = { page, per_page: 30 };
        if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
        const res = await api.get('/audit-log', { params });
        setLogs(res.data.logs || []);
        setTotalPages(res.data.total_pages || 1);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchLogs();
  }, [page, actionFilter]);

  const labels = actionLabels[language] || actionLabels.de;

  return (
    <div data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
            <ClockCounterClockwise size={24} className="inline-block mr-2 text-[#D50C2D]" />
            Audit Log
          </h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{language === 'de' ? 'Alle Aktivitäten im System' : 'All system activities'}</p>
        </div>
        <Select value={actionFilter} onValueChange={v => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[200px] bg-[#0A0A0B] border-white/10" data-testid="audit-action-filter">
            <Funnel size={14} className="mr-2" /><SelectValue placeholder="Alle Aktionen" />
          </SelectTrigger>
          <SelectContent className="bg-[#141416] border-white/10">
            <SelectItem value="all">{language === 'de' ? 'Alle Aktionen' : 'All actions'}</SelectItem>
            <SelectItem value="csv_export">CSV Export</SelectItem>
            <SelectItem value="bulk_import">Bulk Import</SelectItem>
            <SelectItem value="favorite_add">{language === 'de' ? 'Favoriten' : 'Favorites'}</SelectItem>
            <SelectItem value="tags_update">Tags</SelectItem>
            <SelectItem value="template_create">{language === 'de' ? 'Vorlagen' : 'Templates'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 bg-[#141416] border border-white/10 rounded">
          <ClockCounterClockwise size={48} className="mx-auto text-[#52525b] mb-4" />
          <p className="text-[#a1a1aa]">{language === 'de' ? 'Keine Einträge' : 'No entries'}</p>
        </div>
      ) : (
        <div className="bg-[#141416] border border-white/10 rounded overflow-hidden">
          <div className="divide-y divide-white/5">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`audit-entry-${log.id}`}>
                <div className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${actionColors[log.action] || 'text-[#a1a1aa] bg-white/5'}`}>
                  {labels[log.action] || log.action}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-white text-sm">{log.user_name || 'System'}</span>
                  {log.target_id && <span className="text-[#52525b] text-xs ml-2 font-mono">{log.target_type}: {log.target_id.substring(0, 8)}...</span>}
                  {log.details && Object.keys(log.details).length > 0 && (
                    <span className="text-[#52525b] text-xs ml-2">{JSON.stringify(log.details).substring(0, 60)}</span>
                  )}
                </div>
                <span className="text-[#52525b] text-xs font-mono shrink-0">{new Date(log.timestamp).toLocaleString('de-DE')}</span>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <span className="text-[#52525b] text-sm">{language === 'de' ? 'Seite' : 'Page'} {page} / {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="pagination-btn"><CaretLeft size={16} /></button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="pagination-btn"><CaretRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
